import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  OpportunityReferenceCategory,
  OpportunityReferenceDataService,
  OpportunityReferenceValue,
} from '../../../core/services/opportunity-reference-data.service';

interface OpportunityReferencePanel {
  category: OpportunityReferenceCategory;
  titleKey: string;
  values: OpportunityReferenceValue[];
  newValue: string;
  loading: boolean;
  saving: boolean;
}

@Component({
  selector: 'em-opportunity-reference-data',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ConfirmDialogModule,
    InputTextModule,
    ToastModule,
    TranslateModule,
  ],
  templateUrl: './opportunity-reference-data.component.html',
  styleUrl: './opportunity-reference-data.component.scss',
  providers: [ConfirmationService, MessageService],
})
export class OpportunityReferenceDataComponent implements OnInit {
  readonly panels: OpportunityReferencePanel[] = [
    { category: 'durations', titleKey: 'layout.configurations.opportunities.duration', values: [], newValue: '', loading: false, saving: false },
    { category: 'responsibilities', titleKey: 'layout.configurations.opportunities.responsibilities', values: [], newValue: '', loading: false, saving: false },
    { category: 'interpersonal-skills', titleKey: 'layout.configurations.opportunities.interpersonalSkills', values: [], newValue: '', loading: false, saving: false },
    { category: 'behavioral-skills', titleKey: 'layout.configurations.opportunities.behavioralSkills', values: [], newValue: '', loading: false, saving: false },
  ];

  constructor(
    private referenceDataService: OpportunityReferenceDataService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.panels.forEach((panel) => this.loadValues(panel));
  }

  addValue(panel: OpportunityReferencePanel): void {
    const label = panel.newValue.trim();
    if (!label || panel.saving) return;

    panel.saving = true;
    this.referenceDataService.create(panel.category, label).subscribe({
      next: (value) => {
        panel.values = [...panel.values, value].sort((a, b) => a.label.localeCompare(b.label));
        panel.newValue = '';
        panel.saving = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('layout.configurations.opportunities.addSuccessTitle'),
          detail: this.translate.instant('layout.configurations.opportunities.addSuccessDetail'),
        });
      },
      error: () => {
        panel.saving = false;
        this.showError('layout.configurations.opportunities.saveError');
      },
    });
  }

  confirmDelete(event: Event, panel: OpportunityReferencePanel, value: OpportunityReferenceValue): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.translate.instant('layout.configurations.opportunities.deleteTitle'),
      message: this.translate.instant('layout.configurations.opportunities.deleteMessage', { value: value.label }),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteValue(panel, value),
    });
  }

  trackById(_: number, value: OpportunityReferenceValue): number {
    return value.id;
  }

  private loadValues(panel: OpportunityReferencePanel): void {
    panel.loading = true;
    this.referenceDataService.getByCategory(panel.category).subscribe({
      next: (values) => {
        panel.values = values;
        panel.loading = false;
      },
      error: () => {
        panel.loading = false;
        this.showError('layout.configurations.opportunities.loadError');
      },
    });
  }

  private deleteValue(panel: OpportunityReferencePanel, value: OpportunityReferenceValue): void {
    this.referenceDataService.delete(value.id).subscribe({
      next: () => {
        panel.values = panel.values.filter((item) => item.id !== value.id);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('layout.configurations.opportunities.deleteSuccessTitle'),
          detail: this.translate.instant('layout.configurations.opportunities.deleteSuccessDetail'),
        });
      },
      error: () => this.showError('layout.configurations.opportunities.deleteError'),
    });
  }

  private showError(detailKey: string): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('layout.configurations.opportunities.errorTitle'),
      detail: this.translate.instant(detailKey),
    });
  }
}
